import type { ChartOption } from "./chart-option.js"
import { SEQUENTIAL_BLUE, ink, surface, type ThemeMode } from "../palette.js"
import { CHART_TOOLTIP_CSS, FONT_STACK } from "../brand.js"
import { formatValue } from "../format.js"
import type { VizModel } from "../tile-model.js"

/** The name used with ECharts `registerMap`. The frontend must register under the same one */
export const WORLD_MAP_NAME = "csbi-world"

/**
 * ⚠ The default is `properties.name`, and in this base map `name` is the full country name
 * (`"United Kingdom"`) while `iso` is the alpha-3 code (`"GBR"`). Without changing this property,
 * an incoming `GBR` is compared against `"United Kingdom"` and **nothing matches at all — the map
 * renders entirely grey, and nothing errors**. (Hit once in practice: the chart rendered, the
 * console was clean, there was simply no data.)
 */
export const MAP_NAME_PROPERTY = "iso"

export const WORLD_MAP_URL = "/geo/world-countries.topo.json"

export type OffMapEntry = {
  code: string
  values: (number | null)[]
}

export function offMapEntries(model: VizModel, known: ReadonlySet<string>): OffMapEntry[] {
  return model.rawCategories
    .map((code, i) => ({ code, i }))
    .filter(({ code }) => !known.has(code))
    .map(({ code, i }) => ({
      code,
      values: model.series.map((s) => s.data[i] ?? null),
    }))
    .filter((e) => e.values.some((v) => v != null))
}

export type MapView = {
  zoom: number
  center?: [number, number]
}

export const MAP_ZOOM_MIN = 1
export const MAP_ZOOM_MAX = 8
const ZOOM_STEP = 1.6

export const zoomIn = (z: number) => Math.min(MAP_ZOOM_MAX, z * ZOOM_STEP)
export const zoomOut = (z: number) => Math.max(MAP_ZOOM_MIN, z / ZOOM_STEP)

export type MapOptions = {
  mode: ThemeMode
  model: VizModel
  known?: ReadonlySet<string>
  view?: MapView
}

export function mapOption({ mode, model, known = new Set(), view }: MapOptions): ChartOption {
  const c = ink(mode)
  const s = model.series[0]

  const data = model.rawCategories
    .map((code, i) => ({ name: code, value: s?.data[i] ?? null }))
    .filter((d) => d.value != null && known.has(d.name)) as { name: string; value: number }[]

  const values = data.map((d) => d.value)
  const max = values.length > 0 ? Math.max(...values) : 1
  const min = values.length > 0 ? Math.min(...values) : 0

  return {
    backgroundColor: "transparent",
    animationDuration: 240,
    textStyle: {
      color: c.primary,
      fontFamily: FONT_STACK,
    },
    tooltip: {
      trigger: "item",
      backgroundColor: surface(mode),
      borderColor: c.gridline,
      borderWidth: 1,
      padding: [8, 10],
      textStyle: { color: c.primary, fontSize: 12 },
      extraCssText: CHART_TOOLTIP_CSS,
      formatter: (raw: unknown) => {
        const p = raw as { name?: string; value?: number; data?: { name?: string } }
        const code = p.data?.name ?? p.name ?? ""
        const value =
          p.value == null || Number.isNaN(p.value)
            ? `<span style="color:${c.muted}">no data</span>`
            : `<strong style="font-variant-numeric:tabular-nums">${formatValue(p.value, { valueType: model.unit, context: "label" })}</strong>`
        return `<div style="color:${c.secondary};font-size:11px;margin-bottom:2px">${code}</div>${value}`
      },
    },
    legend: { show: false },
    // Sequential ramp: one hue, light to dark. **Never a rainbow** — that reads magnitude as category
    visualMap: {
      type: "continuous",
      min,
      max: max === min ? min + 1 : max,
      calculable: false,
      orient: "horizontal",
      left: 8,
      bottom: 4,
      itemWidth: 10,
      itemHeight: 90,
      textStyle: { color: c.secondary, fontSize: 11 },
      inRange: { color: [...SEQUENTIAL_BLUE] },
      formatter: (v: number) => formatValue(v, { valueType: model.unit, context: "axis" }),
    },
    series: [
      {
        type: "map",
        map: WORLD_MAP_NAME,
        name: s?.name ?? "",
        nameProperty: MAP_NAME_PROPERTY,
        showLegendSymbol: false,
        roam: "move",
        zoom: view?.zoom ?? MAP_ZOOM_MIN,
        ...(view?.center ? { center: view.center } : {}),
        // Antarctica takes the bottom third of the frame and never has data; cropping it gives the space to everything else
        boundingCoords: [
          [-180, 84],
          [180, -58],
        ],
        itemStyle: { areaColor: c.gridline, borderColor: surface(mode), borderWidth: 0.5 },
        emphasis: {
          label: { show: false },
          itemStyle: { areaColor: undefined, borderColor: c.primary, borderWidth: 1 },
        },
        select: { disabled: true },
        label: { show: false },
        data,
      },
    ],
  }
}
